// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { useRouter } from 'vue-router';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import {
  isGlobalError,
  GlobalErrorMessage,
  token,
  isDashboard,
  isCommerce,
  isProduct,
  isCategorie,
  titleSideBar,
} from '@/refs/common.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { useAuthStore } from '@/store/auth.store';
import { AuthService } from '@/services/auth.services';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------
import type { ILoginBody } from '@/types/auth.types';

/**
 * Hook pour gérer la mutation de connexion de l'utilisateur.
 * Utilise `vue-query` pour gérer l'état de la requête.
 * @returns {Object} - Contient la fonction `handleLogin` pour déclencher la mutation.
 */
export const AuthMutation = () => {
  const router = useRouter();
  const authStore = useAuthStore();

  /**
   * Mutation de connexion avec gestion des succès et des erreurs.
   */
  const login = useMutation<any, Error, ILoginBody>({
    mutationFn: (payload: ILoginBody) => {
      return authStore.login(payload);
    },
    /**
     * Gestion en cas de succès de la mutation.
     * - Stocke le token dans les refs globales.
     * - Redirige vers le dashboard.
     * - Met à jour les états de la sidebar.
     * @param {any} data - Réponse renvoyée par la mutation
     */
    onSuccess: (data) => {
      const response = data?.data?.data?.tokens;
      const accessToken = response.accessToken;
      const refreshTokenRefresh = response.refreshToken;

      token.value = {
        custommerId: null,
        accessToken: accessToken,
        refreshToken: refreshTokenRefresh,
      };
    },
    /**
     * Gestion en cas d'erreur lors de la mutation.
     * - Affiche un message d'erreur global temporaire.
     * @param {Error} err - Erreur renvoyée par la mutation
     */
    onError: (err) => {
      const error = err as AxiosError;
      const response = error.response?.data as any;

      const message = response?.message[0];
      console.log('error is ', message);
      isGlobalError.value = true;
      GlobalErrorMessage.value = message;

      setTimeout(() => {
        isGlobalError.value = false;
      }, 1000);
    },
  });

  /**
   * Fonction pour déclencher la mutation de connexion.
   * @param {ILoginBody} item - Données de connexion de l'utilisateur
   */
  const handleLogin = (item: ILoginBody) => {
    return login.mutateAsync(item);
  };

  return {
    handleLogin,
  };
};

/**
 * Hook pour initier la réinitialisation du mot de passe (envoi OTP par email).
 */
export const useInitResetPassword = () => {
  const mutation = useMutation<any, Error, { email: string }>({
    mutationFn: (payload) => AuthService.initResetPassword(payload),
  });
  return {
    initReset: (payload: { email: string }) => mutation.mutateAsync(payload),
    isPending: mutation.isPending,
  };
};

/**
 * Hook pour confirmer la réinitialisation du mot de passe via le code OTP.
 */
export const useConfirmResetPassword = () => {
  const mutation = useMutation<
    any,
    Error,
    { email: string; otp: string; newPassword: string }
  >({
    mutationFn: (payload) => AuthService.confirmResetPassword(payload),
  });
  return {
    confirmReset: (payload: {
      email: string;
      otp: string;
      newPassword: string;
    }) => mutation.mutateAsync(payload),
    isPending: mutation.isPending,
  };
};
